// Watch it build in the canvas, the pure side: a chat's App build state from its own messages, and the checks on what
// an App preview frame reports (Select's selection and the view to keep). Frame messages are untrusted page data.
import { stepKind, stepVerb, type BuildCopy } from '@/components/assistant-ui/build-groups';
import { formatCopy } from '@/i18n/ns';
import { chatIdFromHash } from '@/lib/chat-links';
import type { AppSelection } from '../../types';
import {
  APP_SANDBOX_TOOLS,
  appNotMounted,
  appPreviewTarget,
  liveAppPreviewTarget,
} from './tool-output';

export type AppBuildState = {
  /** The chat is working on its App right now. */
  building: boolean;
  /** The current friendly step ("Writing a file…") while building. */
  step: string | null;
  /** The App's name as the chat gave it; '' until it has one. */
  title: string;
  /** The newest build of the App the canvas can show: a live build or a finished preview. */
  latest: string | null;
  /** The newest finished preview, which also opens after a reload. */
  final: string | null;
  /** Builds of the answers this page saw working, oldest first, each with the order its answer was first seen in: an
   * App's saved editor opened by Change with Chat follows only those its answers made after it opened. */
  recent: readonly RecentBuild[];
  /** The chat's newest preview loaded but drew nothing (its code never mounts the App), and no build has come since. */
  notMounted: boolean;
};
export type RecentBuild = { id: string; finished: boolean; answer: number };

type Part = { type?: string; toolName?: string; args?: unknown; result?: unknown };
type Message = { id?: string; role?: string; parts?: readonly Part[]; content?: readonly Part[] };
const partsOf = (message: Message | undefined) => message?.parts ?? message?.content ?? [];

export function appBuildState(
  messages: readonly Message[],
  threadId: string,
  running: boolean,
  copy: BuildCopy,
  /** When this page first saw an answer working (its order), if it did. */
  answerSeen: (messageId: string) => number | undefined = () => undefined,
): AppBuildState {
  let latest: string | null = null;
  let final: string | null = null;
  const recent: RecentBuild[] = [];
  let notMounted = false;
  let title = '';
  for (const message of messages) {
    const answer = message.id === undefined ? undefined : answerSeen(message.id);
    for (const part of partsOf(message)) {
      if (part.type !== 'tool-call') continue;
      // In the chat's own order: a preview that drew nothing is said until the chat builds or previews again
      // (still working, or done).
      if (appNotMounted(part.toolName, part.result)) notMounted = true;
      else if (part.toolName === 'app_build' || part.toolName === 'app_preview') notMounted = false;
      const finished = appPreviewTarget(part.toolName ?? '', part.result, threadId);
      const target = finished ?? liveAppPreviewTarget(part.toolName, part.result, threadId);
      if (target?.id) {
        latest = target.id;
        if (finished) final = target.id;
        if (answer !== undefined)
          recent.push({ id: target.id, finished: Boolean(finished), answer });
        title = (target as { title?: string }).title || title;
      }
      // The App's name as the chat last gave it to its App tools (or its preview).
      const named = APP_SANDBOX_TOOLS.has(part.toolName ?? '')
        ? (part.args as { title?: unknown })?.title
        : null;
      if (typeof named === 'string' && named.trim()) title = named.trim().slice(0, 200);
    }
  }
  const last = messages.at(-1);
  const calls =
    last?.role === 'assistant' ? partsOf(last).filter((part) => part.type === 'tool-call') : [];
  const building = running && calls.some((call) => APP_SANDBOX_TOOLS.has(call.toolName ?? ''));
  const current = calls.at(-1);
  return {
    building,
    step: building && current ? `${stepVerb(stepKind(current.toolName ?? ''), copy)}…` : null,
    title,
    latest,
    final,
    recent,
    notMounted,
  };
}

const text = (value: unknown, max: number) =>
  typeof value === 'string' && value.trim() && value.length <= max ? value.trim() : undefined;

/** Select's report of the part the person pointed at, bounded; null when it is not one. */
export function readAppSelection(value: unknown): AppSelection | null {
  if (!value || typeof value !== 'object') return null;
  const report = value as Record<string, unknown>;
  const tag = text(report.tag, 40);
  if (!tag) return null;
  const source = text(report.source, 300);
  const located = source && /^[^\s]+:\d+:\d+$/.test(source) ? source : undefined;
  const role = text(report.role, 40);
  const shown = text(report.text, 160);
  const landmark = text(report.landmark, 40);
  return {
    tag,
    ...(located ? { source: located, exact: report.exact === true } : {}),
    ...(role ? { role } : {}),
    ...(shown ? { text: shown } : {}),
    ...(landmark ? { landmark } : {}),
  };
}

/** The view of an App preview kept for its next build: scroll, the scrolled container and the hash route. */
export type PreviewView = {
  x: number;
  y: number;
  hash: string;
  scroller?: { source: string; top: number };
};
const position = (value: unknown) =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 1e7;

export function readPreviewView(value: unknown): PreviewView | null {
  if (!value || typeof value !== 'object') return null;
  const view = value as Record<string, unknown>;
  if (!position(view.x) || !position(view.y)) return null;
  const hash =
    typeof view.hash === 'string' && view.hash.length <= 200 && /^(#\S*)?$/.test(view.hash)
      ? view.hash
      : '';
  const scroller = view.scroller as { source?: unknown; top?: unknown } | undefined;
  const source = text(scroller?.source, 300);
  return {
    x: view.x as number,
    y: view.y as number,
    hash,
    ...(source && position(scroller?.top)
      ? { scroller: { source, top: scroller!.top as number } }
      : {}),
  };
}

/** The composer chip of a selection: its own text when it shows any, else "a part of the App". */
export function appSelectionLabel(
  selection: AppSelection,
  copy: { selectedText: string; selectedPart: string },
) {
  const shown = selection.text?.trim();
  if (!shown) return copy.selectedPart;
  return formatCopy(copy.selectedText, {
    text: shown.length > 40 ? `${shown.slice(0, 39).trimEnd()}…` : shown,
  });
}

/**
 * The thread an App canvas may read for: none while a linked chat is still opening, when the workspace holds a
 * placeholder thread and a preview read for it would only be refused.
 */
export function resolvedThread(threadId: string, chatStatus: string, hash: string) {
  const linked = chatIdFromHash(hash);
  return threadId && chatStatus === 'ready' && (!linked || linked === threadId) ? threadId : null;
}
